import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Download, FileText, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [invoiceToken, setInvoiceToken] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  
  // Check if coming from non-Stripe flow (manual order creation)
  const locationState = location.state;

  useEffect(() => {
    if (sessionId) {
      // Stripe checkout flow - poll for payment status
      pollPaymentStatus(sessionId);
    } else if (orderId || locationState?.orderId) {
      // Manual order flow - show pending payment instructions
      const oid = orderId || locationState?.orderId;
      setOrderData({
        orderId: oid,
        profiles: locationState?.profiles || [],
        total: locationState?.total,
        firstYearFree: locationState?.firstYearFree
      });
      setStatus('pending_payment');
    } else {
      setStatus('error');
    }
  }, [sessionId, orderId, locationState]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/stripe/status/${sessionId}`);

      if (!response.ok) {
        throw new Error('Failed to check status');
      }

      const data = await response.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        // Fetch order details for invoice
        if (data.metadata?.order_id) {
          setOrderData({ orderId: data.metadata.order_id });
        }
        // Save invoice token for download (do NOT overwrite auth token in localStorage)
        if (data.invoice_token) {
          setInvoiceToken(data.invoice_token);
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  const downloadInvoice = () => {
    if (orderData?.orderId && invoiceToken) {
      window.open(`${API}/invoices/${orderData.orderId}?token=${invoiceToken}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-4"
      >
        <div className="bg-white rounded-3xl shadow-soft p-8 text-center">
          {/* Loading State */}
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-[#0A2540]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#0A2540] animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {t('checkout.processing')}
              </h1>
              <p className="text-slate-600">
                {t('checkout.pleaseWait')}
              </p>
            </>
          )}

          {/* Success State (Stripe payment confirmed) */}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {t('checkout.paymentSuccessful')}
              </h1>
              <p className="text-slate-600 mb-6">
                {t('checkout.thankYou')}
              </p>
              {paymentData && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-slate-500 mb-1">{t('checkout.amountPaid')}</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">
                    ${(paymentData.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-3">
                {orderData?.orderId && (
                  <Button 
                    onClick={downloadInvoice}
                    variant="outline" 
                    className="w-full rounded-full gap-2"
                    data-testid="download-invoice-btn"
                  >
                    <Download className="w-4 h-4" />
                    {t('checkout.downloadInvoice')}
                  </Button>
                )}
                <Link to="/owner">
                  <Button className="w-full btn-primary" data-testid="go-dashboard-btn">
                    {t('checkout.goDashboard')}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Pending Payment State (Manual payment flow) */}
          {status === 'pending_payment' && (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CreditCard className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {t('checkout.orderCreated')}
              </h1>
              <p className="text-slate-600 mb-4">
                {t('checkout.pendingPaymentMessage')}
              </p>
              
              {orderData && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left space-y-3">
                  <div>
                    <p className="text-sm text-slate-500">{t('checkout.orderNumber')}</p>
                    <p className="font-mono font-medium text-slate-900">
                      {orderData.orderId}
                    </p>
                  </div>
                  {orderData.total !== undefined && (
                    <div>
                      <p className="text-sm text-slate-500">{t('checkout.totalAmount')}</p>
                      <p className="font-heading text-xl font-bold text-slate-900">
                        ${orderData.total?.toFixed(2)}
                      </p>
                    </div>
                  )}
                  {orderData.profiles?.length > 0 && (
                    <div>
                      <p className="text-sm text-slate-500">{t('checkout.profilesCreated')}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {orderData.profiles.map((p, i) => (
                          <span 
                            key={i} 
                            className="text-xs bg-[#0A2540]/10 text-[#0A2540] px-2 py-1 rounded-full font-mono"
                          >
                            {typeof p === 'string' ? p : p.profile_id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {orderData.firstYearFree && (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded-lg">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('checkout.firstYearFreeNote')}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="bg-[#0A2540]/5 border border-[#E5E7EB] rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-blue-800">
                  <strong>{t('checkout.nextSteps')}:</strong><br/>
                  {t('checkout.nextStepsDetails')}
                </p>
              </div>
              
              <div className="flex flex-col gap-3">
                <Link to="/owner">
                  <Button className="w-full btn-primary" data-testid="go-dashboard-btn">
                    {t('checkout.goDashboard')}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full rounded-full" data-testid="contact-btn">
                    {t('checkout.contactUs')}
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Error States */}
          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {status === 'expired' ? t('checkout.sessionExpired') : t('checkout.paymentIssue')}
              </h1>
              <p className="text-slate-600 mb-6">
                {status === 'expired' 
                  ? t('checkout.sessionExpiredMessage')
                  : t('checkout.paymentIssueMessage')}
              </p>
              <div className="flex flex-col gap-3">
                <Link to="/products">
                  <Button className="w-full btn-primary" data-testid="try-again-btn">
                    {t('checkout.tryAgain')}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full rounded-full" data-testid="contact-support-btn">
                    {t('checkout.contactSupport')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
